import React, { useState, useEffect } from 'react';
import './NewMetricModal.css';
import { METRIC_TYPES, METRIC_TYPE_CONFIGS, getDefaultStatusConfig } from '../constants/metricTypes';
import NumericMetricConfig from './NumericMetricConfig';
import ListMetricConfig from './ListMetricConfig';

const EditMetricModal = ({ isOpen, onClose, onSave, editingMetric }) => {
  const [metricName, setMetricName] = useState('');
  const [unit, setUnit] = useState('');
  const [metricType, setMetricType] = useState(METRIC_TYPES.NUMERIC);
  const [statusConfig, setStatusConfig] = useState(getDefaultStatusConfig(METRIC_TYPES.NUMERIC));

  // Initialize form when editing metric changes
  useEffect(() => {
    if (editingMetric) {
      setMetricName(editingMetric.name || '');
      setUnit(editingMetric.unit || '');
      setMetricType(editingMetric.type || METRIC_TYPES.NUMERIC);
      setStatusConfig(editingMetric.statusConfig || getDefaultStatusConfig(editingMetric.type || METRIC_TYPES.NUMERIC));
    } else {
      // Reset form for new metric
      setMetricName('');
      setUnit('');
      setMetricType(METRIC_TYPES.NUMERIC);
      setStatusConfig(getDefaultStatusConfig(METRIC_TYPES.NUMERIC));
    }
  }, [editingMetric]);

  const handleMetricTypeChange = (newType) => {
    setMetricType(newType);
    setStatusConfig(getDefaultStatusConfig(newType));
  };

  const validateStatusConfig = () => {
    const config = METRIC_TYPE_CONFIGS[metricType];
    return config.validation(statusConfig);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (metricName.trim() && (metricType === METRIC_TYPES.LIST || unit.trim())) {
      const validationErrors = validateStatusConfig();
      
      if (validationErrors.length > 0) {
        alert('Status configuration errors:\n' + validationErrors.join('\n'));
        return;
      }

      const metricData = {
        name: metricName.trim(),
        type: metricType,
        unit: metricType === METRIC_TYPES.LIST ? '' : unit.trim(),
        statusConfig
      };

      try {
        await onSave(metricData);
        resetForm();
        onClose();
      } catch (error) {
        alert('Error saving metric: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setMetricName('');
    setUnit('');
    setMetricType(METRIC_TYPES.NUMERIC);
    setStatusConfig(getDefaultStatusConfig(METRIC_TYPES.NUMERIC));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const isEditing = !!editingMetric;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>{isEditing ? 'Edit Metric' : 'New Metric'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="metric-type-selector">
            <label htmlFor="metricType">Metric Type</label>
            <select
              id="metricType"
              value={metricType}
              onChange={(e) => handleMetricTypeChange(e.target.value)}
              disabled={isEditing} // Prevent type changes when editing
            >
              {Object.entries(METRIC_TYPE_CONFIGS).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.name}
                </option>
              ))}
            </select>
            <div className="metric-type-description">
              {METRIC_TYPE_CONFIGS[metricType].description}
            </div>
            {isEditing && (
              <div className="edit-note">
                <small>Note: Metric type cannot be changed when editing</small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="metricName">Metric Name</label>
            <input
              type="text"
              id="metricName"
              value={metricName}
              onChange={(e) => setMetricName(e.target.value)}
              required
            />
          </div>
          {metricType !== METRIC_TYPES.LIST && (
            <div className="form-group">
              <label htmlFor="unit">Unit</label>
              <input
                type="text"
                id="unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                required
              />
            </div>
          )}
          
          {metricType === METRIC_TYPES.NUMERIC && (
            <NumericMetricConfig
              metricName={metricName}
              unit={unit}
              statusConfig={statusConfig}
              onStatusConfigChange={setStatusConfig}
            />
          )}
          
          {metricType === METRIC_TYPES.LIST && (
            <ListMetricConfig
              metricName={metricName}
              unit={unit}
              statusConfig={statusConfig}
              onStatusConfigChange={setStatusConfig}
            />
          )}
          
          <div className="form-actions">
            <button type="button" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit">
              {isEditing ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMetricModal;
